import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/api";
import {
  LayoutDashboard, PackagePlus, ClipboardCheck, Printer, Warehouse,
  ArrowLeftRight, FlaskConical, GitBranch, FileText, Database, Users, Menu, BookOpen, ChevronDown, ClipboardList, LogOut, X,
} from "lucide-react";
import { useState } from "react";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true },
  { label: "MJ-FORM-01", icon: ClipboardList, id: "mjform01", children: [
    { to: "/receipts", icon: PackagePlus, label: "Penerimaan Barang" },
    { to: "/reports", icon: FileText, label: "Laporan" },
  ] },
  { to: "/stock-card", icon: BookOpen, label: "Kartu Stok MJ-FORM-02" },
  { label: "MJ-FORM-23 Pencatatan Proses", icon: FlaskConical, id: "mjform23", children: [
    { to: "/production", icon: ClipboardList, label: "Formulir Pencatatan Proses" },
    { to: "/production-report", icon: FileText, label: "Laporan" },
  ] },
  { to: "/qc", icon: ClipboardCheck, label: "Pemeriksaan QC" },
  { to: "/labels", icon: Printer, label: "Cetak Label" },
  { to: "/stock", icon: Warehouse, label: "Stok Gudang" },
  { to: "/movements", icon: ArrowLeftRight, label: "Mutasi Bahan" },
  { to: "/traceability", icon: GitBranch, label: "Traceability" },
  { to: "/master", icon: Database, label: "Master Data" },
  { to: "/users", icon: Users, label: "Pengguna & Audit" },
];

const linkCls = (isActive) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
    isActive ? "bg-primary text-primary-foreground" : "text-slate-600 dark:text-slate-300 hover:bg-accent hover:text-accent-foreground"
  }`;

function NavItem({ n, onPick }) {
  return (
    <NavLink to={n.to} end={n.end} onClick={onPick}
      data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.slice(1)}`}
      className={({ isActive }) => linkCls(isActive)}>
      <n.icon className="w-[18px] h-[18px]" /> {n.label}
    </NavLink>
  );
}

function NavGroup({ g, onPick }) {
  const loc = useLocation();
  const active = g.children.some((c) => loc.pathname.startsWith(c.to));
  const [open, setOpen] = useState(true);
  return (
    <div>
      <button type="button" data-testid={`nav-group-${g.id}`} onClick={() => setOpen(!open)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors hover:bg-accent ${active ? "text-primary" : "text-slate-600 dark:text-slate-300"}`}>
        <g.icon className="w-[18px] h-[18px]" /> {g.label}
        <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="ml-4 pl-2 border-l border-slate-200 dark:border-slate-700 space-y-0.5 mt-0.5">
        {g.children.map((c) => <NavItem key={c.to} n={c} onPick={onPick} />)}
      </div>}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary text-primary-foreground grid place-items-center font-heading font-extrabold text-sm">TP</div>
          <div>
            <div className="font-heading font-bold leading-tight text-sm">UD TIGA PUTRA</div>
            <div className="text-[11px] text-muted-foreground">Traceability MJ-FORM-01</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
        {NAV.map((n) => n.children
          ? <NavGroup key={n.id} g={n} onPick={() => setOpen(false)} />
          : <NavItem key={n.to} n={n} onPick={() => setOpen(false)} />)}
      </nav>
      <div className="px-4 py-4 border-t border-slate-200 dark:border-slate-800">
        <div className="text-xs font-semibold">{user?.name}</div>
        <div className="text-[11px] text-muted-foreground mb-2">{ROLE_LABELS[user?.role]}</div>
        <button data-testid="logout-btn" onClick={() => { logout(); nav("/login"); }}
          className="flex items-center gap-2 text-xs text-red-600 hover:underline">
          <LogOut className="w-4 h-4" /> Keluar
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden lg:flex w-64 border-r border-slate-200 dark:border-slate-800 bg-card fixed inset-y-0">{SidebarInner}</aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-card h-full shadow-xl">{SidebarInner}</div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
          <button className="absolute top-4 right-4 text-white" onClick={() => setOpen(false)}><X /></button>
        </div>
      )}
      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header className="h-14 border-b border-slate-200 dark:border-slate-800 bg-card/95 backdrop-blur sticky top-0 z-30 flex items-center px-4 gap-3">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu /></button>
          <div className="text-sm text-muted-foreground">{new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
          <div className="ml-auto text-xs font-semibold px-3 py-1 rounded-full bg-accent text-accent-foreground">{ROLE_LABELS[user?.role]}</div>
        </header>
        <main className="flex-1 p-4 sm:p-6 max-w-[1400px] w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}
